/**
 * List-page state that lives in the URL (OPEN-191 / OPEN-204; first built in web-admin after
 * Corbin, 2026-10-20: going into an operator and back lost the "devon" search and the filters).
 *
 * Each filter, tab and sort is a query parameter, so browser back, a refresh and a shared link
 * all restore the list exactly. Values equal to their default are left out of the URL, and
 * changes REPLACE the history entry, so typing a search does not stack one back-step per keystroke.
 *
 * A list row opens its detail page with `listFrom(location)` as router state; the detail page's
 * own "Back" goes to `useBackTo(<list path>)`, which is that URL (search and filters included),
 * or the bare list path when the page was opened directly.
 *
 * web-shared does not depend on react-router (every app pins its own copy), so the hooks come from
 * `createUrlState`, which each app calls once with its router's `useSearchParams` and
 * `useLocation` (see web-admin `lib/url-state.ts`). The sort logic is plain functions, tested here.
 */

/** The parts of a router location these helpers read. */
export interface UrlLocation {
  readonly pathname: string;
  readonly search: string;
  readonly state?: unknown;
}

type ParamsUpdater = (prev: URLSearchParams) => URLSearchParams;
/** react-router's `useSearchParams`, narrowed to what these helpers use. */
export type UseSearchParamsHook = () => readonly [
  URLSearchParams,
  (next: ParamsUpdater, opts: { replace: boolean }) => void,
];

export interface ListFromState {
  readonly from: string;
}

/** Router state for opening a detail page from a list: remember the list's exact URL. */
export function listFrom(location: UrlLocation): ListFromState {
  return { from: `${location.pathname}${location.search}` };
}

/**
 * Where a detail page's Back goes: the list URL it was opened from, when that URL is under
 * `fallback` or one of `allowed` (a detail page reached from more than one section), else
 * `fallback`. Anything else in router state is ignored, so Back never leaves the app.
 */
export function backTarget(
  location: UrlLocation,
  fallback: string,
  allowed: readonly string[] = [],
): string {
  const from = (location.state as Partial<ListFromState> | null | undefined)?.from;
  if (typeof from !== 'string' || !from.startsWith('/')) return fallback;
  const ok = [fallback, ...allowed].some((prefix) => from.startsWith(prefix));
  return ok ? from : fallback;
}

// ── Sort (OPEN-204) ────────────────────────────────────────────────────────────

export type SortDir = 'asc' | 'desc';

export interface SortState<K extends string> {
  readonly key: K;
  readonly dir: SortDir;
}

/**
 * The query parameters a sort uses: `?sort=&dir=` by default; a page with more than one sortable
 * table names each one, e.g. `name = 'fees'` → `?fees=&feesDir=`.
 */
export function sortParamNames(name = 'sort'): { readonly key: string; readonly dir: string } {
  return name === 'sort' ? { key: 'sort', dir: 'dir' } : { key: name, dir: `${name}Dir` };
}

/**
 * The sort a URL asks for, kept to `allowed` keys (anything else falls back to the default, so a
 * hand-edited link can't sort by a column the page doesn't offer). A null default means "the
 * order the list arrives in": no column is sorted until a header is clicked.
 */
export function parseSort<K extends string>(
  params: URLSearchParams,
  allowed: readonly K[],
  def: SortState<K> | null,
  name = 'sort',
): SortState<K> | null {
  const names = sortParamNames(name);
  const raw = params.get(names.key);
  const key = allowed.find((k) => k === raw) ?? def?.key;
  if (key === undefined) return null;
  const dirRaw = params.get(names.dir);
  const dir: SortDir =
    dirRaw === 'asc' || dirRaw === 'desc' ? dirRaw : key === def?.key ? def.dir : 'asc';
  return { key, dir };
}

/** A header click: the same column reverses; a new column starts ascending. */
export function nextSort<K extends string>(current: SortState<K> | null, clicked: K): SortState<K> {
  if (current?.key === clicked)
    return { key: clicked, dir: current.dir === 'asc' ? 'desc' : 'asc' };
  return { key: clicked, dir: 'asc' };
}

/** `aria-sort` for a column header. */
export function ariaSort<K extends string>(
  current: SortState<K> | null,
  column: K,
): 'ascending' | 'descending' | 'none' {
  if (current?.key !== column) return 'none';
  return current.dir === 'asc' ? 'ascending' : 'descending';
}

/**
 * Sort rows in the browser (for lists the page already holds in full). Text compares
 * case-insensitively with numbers in text ordered numerically ("T-2" before "T-10"); null and
 * empty sort last in either direction; ties keep their original order. No sort (null) keeps the
 * order the rows arrived in.
 */
export function sortRows<T, K extends string>(
  rows: readonly T[],
  sort: SortState<K> | null,
  value: (row: T, key: K) => string | number | null | undefined,
): T[] {
  if (sort === null) return [...rows];
  const collator = new Intl.Collator('en-US', { numeric: true, sensitivity: 'base' });
  const sign = sort.dir === 'asc' ? 1 : -1;
  return rows
    .map((row, i) => ({ row, i, v: value(row, sort.key) }))
    .sort((a, b) => {
      const aEmpty = a.v === null || a.v === undefined || a.v === '';
      const bEmpty = b.v === null || b.v === undefined || b.v === '';
      if (aEmpty || bEmpty) return aEmpty === bEmpty ? a.i - b.i : aEmpty ? 1 : -1;
      const cmp =
        typeof a.v === 'number' && typeof b.v === 'number'
          ? a.v - b.v
          : collator.compare(String(a.v), String(b.v));
      return cmp !== 0 ? sign * cmp : a.i - b.i;
    })
    .map((x) => x.row);
}

// ── Hooks ──────────────────────────────────────────────────────────────────────

/**
 * The hooks each app binds once. Arrow-function members (not methods), so an app can destructure
 * them without `@typescript-eslint/unbound-method`: none of them reads `this`.
 */
export interface UrlStateHooks {
  /**
   * One string query parameter with a default; the setter drops it from the URL at the default,
   * and clears `resets` (the page by default) in the SAME update, so one click never needs two URL
   * writes (a second write in the same tick would start from the stale params and undo the first).
   */
  readonly useUrlParam: (
    key: string,
    def?: string,
    resets?: readonly string[],
  ) => [string, (v: string) => void];
  /** A boolean toggle as `?key=1`. */
  readonly useUrlFlag: (
    key: string,
    resets?: readonly string[],
  ) => [boolean, (on: boolean) => void];
  /** A whole-number parameter (a page index), default 0. */
  readonly useUrlNumber: (key: string, resets?: readonly string[]) => [number, (n: number) => void];
  /** Where this detail page's Back goes (see `backTarget`). */
  readonly useBackTo: (fallback: string, opts?: { readonly allowed?: readonly string[] }) => string;
  /**
   * `?sort=&dir=` for a sortable table: the current sort (kept to `allowed`) and a header-click
   * handler. A new sort returns to the first page (`resets`, default `page`). `name` gives a
   * second table on the same page its own parameters (see `sortParamNames`).
   */
  readonly useUrlSort: <K extends string>(
    allowed: readonly K[],
    def: SortState<K> | null,
    resets?: readonly string[],
    name?: string,
  ) => { readonly sort: SortState<K> | null; readonly toggle: (column: K) => void };
}

export function createUrlState(router: {
  readonly useSearchParams: UseSearchParamsHook;
  readonly useLocation: () => UrlLocation;
}): UrlStateHooks {
  function useUrlParam(
    key: string,
    def = '',
    resets: readonly string[] = ['page'],
  ): [string, (value: string) => void] {
    const [params, setParams] = router.useSearchParams();
    const value = params.get(key) ?? def;
    const set = (next: string): void => {
      setParams(
        (prev) => {
          const p = new URLSearchParams(prev);
          if (next === def || next === '') p.delete(key);
          else p.set(key, next);
          // A filter change returns to the first page of a paged list (and closes what it lists).
          for (const r of resets) if (r !== key) p.delete(r);
          return p;
        },
        { replace: true },
      );
    };
    return [value, set];
  }

  function useUrlFlag(
    key: string,
    resets: readonly string[] = ['page'],
  ): [boolean, (on: boolean) => void] {
    const [v, set] = useUrlParam(key, '', resets);
    return [v === '1', (on): void => set(on ? '1' : '')];
  }

  function useUrlNumber(
    key: string,
    resets: readonly string[] = [],
  ): [number, (n: number) => void] {
    const [v, set] = useUrlParam(key, '', resets);
    const n = Number.parseInt(v, 10);
    return [Number.isFinite(n) && n > 0 ? n : 0, (next): void => set(next > 0 ? String(next) : '')];
  }

  function useBackTo(fallback: string, opts?: { readonly allowed?: readonly string[] }): string {
    return backTarget(router.useLocation(), fallback, opts?.allowed);
  }

  function useUrlSort<K extends string>(
    allowed: readonly K[],
    def: SortState<K> | null,
    resets: readonly string[] = ['page'],
    name = 'sort',
  ): { readonly sort: SortState<K> | null; readonly toggle: (column: K) => void } {
    const [params, setParams] = router.useSearchParams();
    const names = sortParamNames(name);
    const sort = parseSort(params, allowed, def, name);
    const toggle = (column: K): void => {
      const next = nextSort(sort, column);
      setParams(
        (prev) => {
          const p = new URLSearchParams(prev);
          // The default sort is left out of the URL, like any default.
          if (def !== null && next.key === def.key && next.dir === def.dir) {
            p.delete(names.key);
            p.delete(names.dir);
          } else {
            p.set(names.key, next.key);
            p.set(names.dir, next.dir);
          }
          for (const r of resets) p.delete(r);
          return p;
        },
        { replace: true },
      );
    };
    return { sort, toggle };
  }

  return { useUrlParam, useUrlFlag, useUrlNumber, useBackTo, useUrlSort };
}
